import React from 'react';
import { createPortal } from 'react-dom';
import './BirthdateField.css';

interface BirthdateFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const BirthdateField: React.FC<BirthdateFieldProps> = ({
  id,
  value,
  onChange,
  placeholder = 'Select your birthdate',
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties>({});
  const normalizedValue = React.useMemo(() => normalizeBirthdate(value), [value]);
  const selectedDate = React.useMemo(
    () => (normalizedValue ? fromDateKey(normalizedValue) : null),
    [normalizedValue],
  );
  const [visibleMonth, setVisibleMonth] = React.useState(() => (
    selectedDate || startOfMonth(addYears(new Date(), -25))
  ));

  React.useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(startOfMonth(selectedDate));
    }
  }, [selectedDate]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const updatePopoverPosition = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.min(280, rect.width);
      const maxLeft = window.innerWidth - width - 12;
      const left = Math.max(12, Math.min(rect.left, maxLeft));
      const top = rect.bottom + 8;
      const spaceBelow = window.innerHeight - top - 12;
      const maxHeight = Math.max(220, Math.min(360, spaceBelow));

      setPopoverStyle({
        position: 'fixed',
        top,
        left,
        width,
        maxHeight,
      });
    };

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target)
        && !popoverRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const monthCells = React.useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 121 }, (_, index) => currentYear - index);
  }, []);
  const maxDateKey = toDateKey(startOfDay(new Date()));

  const handleSelectDate = (date: Date) => {
    const dateKey = toDateKey(date);
    if (dateKey > maxDateKey) {
      return;
    }
    onChange(dateKey);
    setIsOpen(false);
  };

  const portalRoot = document.getElementById('v2-portal-root') ?? document.body;

  const popover = isOpen ? (
    <div
      ref={popoverRef}
      className="v2-birthdate-field__popover"
      style={popoverStyle}
      role="dialog"
      aria-label="Choose birthdate"
    >
      <div className="v2-birthdate-field__nav">
        <select
          aria-label="Birth month"
          value={visibleMonth.getMonth()}
          onChange={(event) => {
            const next = new Date(visibleMonth);
            next.setMonth(Number(event.target.value));
            setVisibleMonth(startOfMonth(next));
          }}
        >
          {MONTHS.map((month, index) => (
            <option key={month} value={index}>{month}</option>
          ))}
        </select>
        <select
          aria-label="Birth year"
          value={visibleMonth.getFullYear()}
          onChange={(event) => {
            const next = new Date(visibleMonth);
            next.setFullYear(Number(event.target.value));
            setVisibleMonth(startOfMonth(next));
          }}
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      <div className="v2-birthdate-field__weekdays">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>

      <div className="v2-birthdate-field__days">
        {monthCells.map((cell) => {
          const isSelected = normalizedValue === cell.dateKey;
          const isDisabled = !cell.inMonth || cell.dateKey > maxDateKey;

          if (!cell.inMonth) {
            return (
              <span key={cell.dateKey} className="v2-birthdate-field__day--muted">
                {cell.date.getDate()}
              </span>
            );
          }

          return (
            <button
              type="button"
              key={cell.dateKey}
              disabled={isDisabled}
              onClick={() => handleSelectDate(cell.date)}
              className={[
                isSelected ? 'v2-birthdate-field__day--selected' : '',
                isDisabled ? 'v2-birthdate-field__day--disabled' : '',
              ].filter(Boolean).join(' ')}
            >
              {cell.date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div className="v2-birthdate-field" ref={containerRef}>
      <div className="v2-birthdate-field__control">
        <button
          type="button"
          id={id}
          className={`v2-birthdate-field__display ${normalizedValue ? '' : 'v2-birthdate-field__display--empty'}`.trim()}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          {normalizedValue ? formatBirthdateLabel(normalizedValue) : placeholder}
        </button>
        <button
          type="button"
          className="v2-birthdate-field__trigger"
          aria-label="Open birthdate calendar"
          onClick={() => setIsOpen((open) => !open)}
        >
          📅
        </button>
      </div>

      {popover && createPortal(popover, portalRoot)}
    </div>
  );
};

interface MonthCell {
  date: Date;
  dateKey: string;
  inMonth: boolean;
}

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function startOfMonth(date: Date): Date {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
}

function addYears(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + amount);
  return startOfDay(next);
}

function addDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return startOfDay(next);
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return startOfDay(new Date(year, month - 1, day));
}

function buildMonthCells(monthDate: Date): MonthCell[] {
  const monthStart = startOfMonth(monthDate);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const cells: MonthCell[] = [];

  for (let index = 0; index < 42; index += 1) {
    const date = addDays(gridStart, index);
    cells.push({
      date,
      dateKey: toDateKey(date),
      inMonth: date.getMonth() === monthStart.getMonth(),
    });
  }

  return cells;
}

function formatBirthdateLabel(dateKey: string): string {
  const date = fromDateKey(dateKey);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function normalizeBirthdate(value?: string | null): string {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return '';
  }

  const date = fromDateKey(trimmed);
  if (Number.isNaN(date.getTime()) || toDateKey(date) !== trimmed) {
    return '';
  }

  return trimmed;
}

export default BirthdateField;
